import axiosInstance from "../../config/axiosInstance";
import { USER_API } from "../../Utils/constants/apis.urls";

export const getRaces = async (filters = {}) => {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) 
      throw new Error("User not authenticated");

    // Only include date or venue if provided
    const params = {};
    if (filters.date) params.date = filters.date;
    if (filters.venue) params.venue = filters.venue;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;

    const response = await axiosInstance.get(USER_API.RACES, {
      params, // query params
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data; // { message, count, filters_applied, results }
  } catch (error) {
    console.error("Error fetching races:", error);
    throw error.response?.data || { error: "Failed to fetch races" };
  }
};

export const getComparison = async (filters = {}) => {
  try {
    const token = localStorage.getItem("access_token");

    const params = {};
    if (filters.date) params.date = filters.date;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;

    const response = await axiosInstance.get(USER_API.COMPARISON, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Comparison Error:", error);
    throw error;
  }
};

export const getComparisonStats = async () => {
  try {
    const response = await axiosInstance.get(USER_API.COMPARISON_STATS);
    return response.data;
  } catch (error) {
    console.error("Comparison Stats Error:", error);
    throw error.response?.data || { error: "Failed to fetch comparison stats" };
  }
};

export const getVenueVisibility = async (date) => {
  try {
    const response = await axiosInstance.get(USER_API.VENUE_VISIBILITY, {
      params: { date },
    });

    return response.data;
  } catch (error) {
    console.error("Venue Visibility Error:", error);
    throw error.response?.data || { error: "Failed to fetch venue visibility" };
  }
};

export const updateVenueVisibility = async (date, venues) => {
  try {
    const response = await axiosInstance.put(USER_API.VENUE_VISIBILITY, {
      date,
      venues,
    });

    return response.data;
  } catch (error) {
    console.error("Update Venue Visibility Error:", error);
    throw error.response?.data || { error: "Failed to update venue visibility" };
  }
};

export const getSubscriptionPlans = async () => {
  try {
    const response = await axiosInstance.get(USER_API.SUBSCRIPTION_PLANS);
    return response.data;
  } catch (error) {
    console.error("Subscription Plans Error:", error);
    throw error.response?.data || { error: "Failed to fetch subscription plans" };
  }
};

export const getSubscriptionPricePoints = async () => {
  try {
    const response = await axiosInstance.get(USER_API.SUBSCRIPTION_PRICE_POINTS);
    return response.data?.price_points || [];
  } catch (error) {
    console.error("Subscription Price Points Error:", error);
    throw error.response?.data || { error: "Failed to fetch price points" };
  }
};

export const createSubscriptionPlan = async (plan) => {
  try {
    const response = await axiosInstance.post(USER_API.SUBSCRIPTION_PLANS, plan);
    return response.data;
  } catch (error) {
    console.error("Create Subscription Plan Error:", error);
    throw error.response?.data || { error: "Failed to create subscription plan" };
  }
};

export const updateSubscriptionPlan = async (planId, plan) => {
  try {
    const response = await axiosInstance.patch(
      `${USER_API.SUBSCRIPTION_PLANS}${planId}/`,
      plan
    );
    return response.data;
  } catch (error) {
    console.error("Update Subscription Plan Error:", error);
    throw error.response?.data || { error: "Failed to update subscription plan" };
  }
};

export const deleteSubscriptionPlan = async (planId) => {
  try {
    await axiosInstance.delete(`${USER_API.SUBSCRIPTION_PLANS}${planId}/`);
  } catch (error) {
    console.error("Delete Subscription Plan Error:", error);
    throw error.response?.data || { error: "Failed to delete subscription plan" };
  }
};

export const getAdminUsers = async (search = "", page = 1, page_size = 15) => {
  try {
    const params = { page, page_size };
    if (search) params.search = search;

    const response = await axiosInstance.get(USER_API.ADMIN_USERS, { params });
    return response.data;
  } catch (error) {
    console.error("Admin Users Error:", error);
    throw error.response?.data || { error: "Failed to fetch users" };
  }
};

export const createAdminUser = async (user) => {
  try {
    const response = await axiosInstance.post(USER_API.ADMIN_USERS, user);
    return response.data;
  } catch (error) {
    console.error("Create Admin User Error:", error);
    throw error.response?.data || { error: "Failed to create user" };
  }
};

export const updateAdminUser = async (userId, user) => {
  try {
    const response = await axiosInstance.patch(
      `${USER_API.ADMIN_USERS}${userId}/`,
      user
    );
    return response.data;
  } catch (error) {
    console.error("Update Admin User Error:", error);
    throw error.response?.data || { error: "Failed to update user" };
  }
};

export const deleteAdminUser = async (userId) => {
  try {
    await axiosInstance.delete(`${USER_API.ADMIN_USERS}${userId}/`);
  } catch (error) {
    console.error("Delete Admin User Error:", error);
    throw error.response?.data || { error: "Failed to delete user" };
  }
};

export const getModelTrainingHistory = async () => {
  try {
    const response = await axiosInstance.get(USER_API.FEATURE_IMPORTANCE);
    return response.data;
  } catch (error) {
    console.error("Model Training History Error:", error);
    throw error.response?.data || { error: "Failed to fetch model weightage values" };
  }
};

export const getAccuracyTrends = async () => {
  try {
    const response = await axiosInstance.get(USER_API.ACCURACY_TRENDS);
    return response.data;
  } catch (error) {
    console.error("Accuracy Trends Error:", error);
    throw error.response?.data || { error: "Failed to fetch accuracy trends" };
  }
};

export const getNotifications = async (page = 1, pageSize = 25, search = "", filterType = "") => {
  try {
    let url = `${USER_API.NOTIFICATIONS.LIST}?page=${page}&page_size=${pageSize}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (filterType) {
      url += `&filter_type=${encodeURIComponent(filterType)}`;
    }
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error("Get Notifications Error:", error);
    throw error.response?.data || { error: "Failed to fetch notifications" };
  }
};

export const sendNotification = async (payload) => {
  try {
    const response = await axiosInstance.post(USER_API.NOTIFICATIONS.LIST, payload);
    return response.data;
  } catch (error) {
    console.error("Send Notification Error:", error);
    throw error.response?.data || { error: "Failed to send notification" };
  }
};

export const getLandingPageStats = async () => {
  try {
    const response = await axiosInstance.get(USER_API.LANDING_PAGE_STATS);
    return response.data;
  } catch (error) {
    console.error("Get Landing Page Stats Error:", error);
    throw error.response?.data || { error: "Failed to fetch stats" };
  }
};

export const createLandingPageStat = async (stat) => {
  try {
    const response = await axiosInstance.post(USER_API.LANDING_PAGE_STATS, stat);
    return response.data;
  } catch (error) {
    console.error("Create Landing Page Stat Error:", error);
    throw error.response?.data || { error: "Failed to create stat" };
  }
};

export const updateLandingPageStats = async (statsList) => {
  try {
    const response = await axiosInstance.put(USER_API.LANDING_PAGE_STATS, statsList);
    return response.data;
  } catch (error) {
    console.error("Update Landing Page Stats Error:", error);
    throw error.response?.data || { error: "Failed to update stats" };
  }
};

export const deleteLandingPageStat = async (id) => {
  try {
    const response = await axiosInstance.delete(`${USER_API.LANDING_PAGE_STATS}${id}/`);
    return response.data;
  } catch (error) {
    console.error("Delete Landing Page Stat Error:", error);
    throw error.response?.data || { error: "Failed to delete stat" };
  }
};

export const getAdminSubscribers = async (search = "", page = 1, page_size = 15) => {
  try {
    const params = { page, page_size };
    if (search) params.search = search;
    const response = await axiosInstance.get(USER_API.ADMIN_SUBSCRIBERS, { params });
    return response.data;
  } catch (error) {
    console.error("Admin Subscribers Error:", error);
    throw error.response?.data || { error: "Failed to fetch subscribers" };
  }
};

export const getAdminPayments = async (filters = {}, page = 1, page_size = 15) => {
  try {
    const params = { page, page_size };
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.search) params.search = filters.search;
    const response = await axiosInstance.get(USER_API.ADMIN_PAYMENTS, { params });
    return response.data;
  } catch (error) {
    console.error("Admin Payments Error:", error);
    throw error.response?.data || { error: "Failed to fetch payments" };
  }
};

export const exportPredictions = async (fromDate, toDate) => {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) throw new Error("User not authenticated");

    const response = await axiosInstance.get(USER_API.EXPORT_PREDICTIONS, {
      params: { from_date: fromDate, to_date: toDate },
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });

    // Derive filename from Content-Disposition header or build a fallback
    const disposition = response.headers["content-disposition"] || "";
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch
      ? filenameMatch[1]
      : `predictions_${fromDate}_to_${toDate}.xlsx`;

    // Trigger browser download
    const url = URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Export Predictions Error:", error);
    throw error.response?.data || { error: "Failed to export predictions" };
  }
};

export const exportUsers = async (fromDate, toDate) => {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) throw new Error("User not authenticated");

    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    const response = await axiosInstance.get(USER_API.EXPORT_USERS, {
      params,
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"] || "";
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch
      ? filenameMatch[1]
      : `users_export.xlsx`;

    const url = URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Export Users Error:", error);
    throw error.response?.data || { error: "Failed to export users" };
  }
};
